import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg, fileUrl, avatarSrc } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { MapView } from "@/components/MapView";
import { ImageUpload } from "@/components/ImageUpload";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CANCEL_REASONS } from "@/lib/constants";
import { toast } from "sonner";
import {
  MapPin, ArrowRight, Loader2, CheckCircle2, Circle, User, MessageSquare, Flag, Truck,
  ShieldCheck, Car, Phone, Camera, PackageCheck,
} from "lucide-react";

const TIMELINE_STEPS = ["Request Created", "Traveller Connected", "Accepted", "Package Picked Up", "In Transit", "Arrived", "Delivered"];
const STATUS_ORDER = { Requested: 1, Matched: 1, Accepted: 2, "Pickup Pending": 2, "Picked Up": 3, "In Transit": 4, Arrived: 5, "Out for Delivery": 5, Delivered: 6, Cancelled: 2 };
const REPORT_REASONS = ["Suspicious behaviour", "Prohibited item", "Fraud or scam", "Harassment", "Other"];

const getPos = () => new Promise((res) => {
  if (!navigator.geolocation) return res({});
  navigator.geolocation.getCurrentPosition(
    (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
    () => res({}), { timeout: 5000 }
  );
});

const Photo = ({ id, alt, className }) => id ? <img src={fileUrl(id)} alt={alt} className={className || "rounded-xl border border-slate-200 max-h-48 w-full object-cover"} /> : null;

const QRCode = ({ value, size = 130 }) => (
  <img alt="QR code" width={size} height={size}
    src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=6&data=${encodeURIComponent(value)}`}
    className="rounded-lg border border-slate-200 bg-white mx-auto" />
);

export default function DeliveryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reportDesc, setReportDesc] = useState("");
  const [pickupOpen, setPickupOpen] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [cReason, setCReason] = useState("");
  const [cOther, setCOther] = useState("");
  const [pickupPin, setPickupPin] = useState("");
  const [deliverPin, setDeliverPin] = useState("");
  const [rating, setRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/deliveries/${id}`);
      setD(data.delivery);
      if (data.delivery.role === "sender") {
        const conns = await api.get("/connections/mine");
        setOffers(conns.data.connections.filter((c) => c.delivery?.id === id && c.status === "pending" && c.my_role === "sender"));
      }
    } catch (e) { toast.error(errMsg(e)); navigate("/my-deliveries"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  // Traveller shares real GPS automatically only while status is between pickup and delivery.
  const watchRef = useRef(null);
  const lastSent = useRef(0);
  const trackingActive = !!d && d.role === "traveller" && ["Picked Up", "In Transit", "Arrived", "Out for Delivery"].includes(d.status);
  useEffect(() => {
    if (!trackingActive) return;
    if (!navigator.geolocation) { toast.error("Live location is not supported on this device."); return; }
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const now = Date.now();
        if (now - lastSent.current < 10000) return;
        lastSent.current = now;
        api.post(`/deliveries/${id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {});
      },
      (err) => { if (err.code === 1) toast.error("Location permission denied. Enable it to share live location."); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    return () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; };
  }, [trackingActive, id]); // eslint-disable-line

  // Sender polls for the traveller's live location while tracking is active.
  const senderTracking = !!d && d.role === "sender" && d.live_tracking;
  useEffect(() => {
    if (!senderTracking) return;
    const t = setInterval(() => { load(); }, 12000);
    return () => clearInterval(t);
  }, [senderTracking]); // eslint-disable-line

  const respond = async (cid, action) => {
    setBusy(true);
    try { await api.post(`/connections/${cid}/${action}`); toast.success(action === "accept" ? "Connection accepted." : "Offer declined."); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/deliveries/${id}`, { status }); toast.success(`Marked ${status}.`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirmPickup = async () => {
    if (!pickupPin.trim()) { toast.error("Enter the 6-digit pickup PIN from the sender"); return; }
    setBusy(true);
    try {
      const pos = await getPos();
      await api.post(`/deliveries/${id}/confirm-pickup`, { photo_id: photo, pin: pickupPin.trim(), lat: pos.lat ?? d.pickup_lat, lng: pos.lng ?? d.pickup_lng, address: d.pickup });
      toast.success("Pickup confirmed."); setPickupOpen(false); setPhoto(null); setPickupPin(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirmDelivery = async () => {
    if (!deliverPin.trim()) { toast.error("Enter the 6-digit delivery PIN from the recipient"); return; }
    setBusy(true);
    try {
      const pos = await getPos();
      await api.post(`/deliveries/${id}/confirm-delivery`, { photo_id: photo, pin: deliverPin.trim(), lat: pos.lat ?? d.dest_lat, lng: pos.lng ?? d.dest_lng, address: d.destination });
      toast.success("Delivery confirmed."); setDeliverOpen(false); setPhoto(null); setDeliverPin(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitReview = async () => {
    if (!rating) { toast.error("Please select a star rating."); return; }
    setBusy(true);
    try {
      await api.post(`/deliveries/${id}/reviews`, { rating, comment: reviewComment.trim() });
      toast.success("Thanks for your review!");
      setRating(0); setReviewComment(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const r = cReason === "Other" ? cOther : cReason;
      await api.post(`/deliveries/${id}/cancel`, { reason: r });
      toast.success(d.role === "traveller" ? "You have backed out. The delivery is open again for other travellers." : "Delivery cancelled.");
      setCancelOpen(false);
      if (d.role === "traveller") navigate("/my-trips"); else load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitReport = async () => {
    if (!reason) { toast.error("Select a reason"); return; }
    try {
      await api.post("/reports", { target_type: "delivery", target_id: id, reason, description: reportDesc });
      toast.success("Report submitted. Our team will review it.");
      setReportOpen(false); setReason(""); setReportDesc("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>;
  if (!d) return null;

  const doneStates = new Set((d.timeline || []).map((t) => t.state));
  const curOrder = STATUS_ORDER[d.status] ?? 0;
  const tv = d.trip_info || {};
  const canConfirmDeliver = ["Picked Up", "In Transit", "Arrived", "Out for Delivery"].includes(d.status);
  const canCancel = (d.role === "sender" || d.role === "traveller") && ["Requested", "Matched", "Accepted"].includes(d.status);
  const travellerPickupStage = d.role === "traveller" && ["Accepted", "Matched", "Pickup Pending"].includes(d.status);
  const travellerDeliverStage = d.role === "traveller" && canConfirmDeliver;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader title="Pack Mate Delivery" subtitle={<span className="font-mono">{d.id}</span>} action={<StatusBadge status={d.status} />} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {(travellerPickupStage || travellerDeliverStage) && (
            <SectionCard title={<span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> {travellerPickupStage ? "Enter pickup PIN" : "Enter delivery PIN"}</span>}>
              <div className="rounded-xl border-2 border-primary/30 bg-accent/60 p-4 space-y-3" data-testid="traveller-handover-card">
                <p className="text-sm text-slate-600">
                  {travellerPickupStage
                    ? "Ask the sender for the 6-digit pickup code and enter it here to confirm you've collected the package."
                    : "Ask the recipient for the 6-digit delivery code and enter it here to complete the delivery."}
                </p>
                {((travellerPickupStage && d.pickup_lat != null) || (travellerDeliverStage && d.dest_lat != null)) && (
                  <div className="space-y-1.5" data-testid="handover-map">
                    <Label className="text-sm flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" /> {travellerPickupStage ? "Pickup location" : "Destination"}</Label>
                    <p className="text-xs text-slate-500">{travellerPickupStage ? d.pickup : d.destination}</p>
                    <MapView
                      origin={travellerPickupStage ? { lat: d.pickup_lat, lng: d.pickup_lng } : { lat: d.dest_lat, lng: d.dest_lng }}
                      destination={null}
                      originLabel={travellerPickupStage ? "Pickup" : "Destination"}
                      height={160}
                      showRoute={false}
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-sm">{travellerPickupStage ? "Pickup PIN" : "Delivery PIN"} <span className="text-rose-500">*</span></Label>
                  <Input data-testid="inline-pin-input"
                    value={travellerPickupStage ? pickupPin : deliverPin}
                    onChange={(e) => travellerPickupStage ? setPickupPin(e.target.value) : setDeliverPin(e.target.value)}
                    placeholder="6-digit code" inputMode="numeric" maxLength={6}
                    className="rounded-xl font-mono tracking-[0.4em] text-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{travellerPickupStage ? "Package photo at pickup" : "Delivery proof photo"} <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <ImageUpload value={photo} onChange={setPhoto} purpose={travellerPickupStage ? "pickup" : "delivery"} testid="inline-handover-photo" />
                </div>
                <Button onClick={travellerPickupStage ? confirmPickup : confirmDelivery} disabled={busy} className="w-full rounded-xl" data-testid="inline-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (travellerPickupStage ? <><Camera className="w-4 h-4" /> Confirm Pickup</> : <><PackageCheck className="w-4 h-4" /> Confirm Delivery</>)}
                </Button>
              </div>
            </SectionCard>
          )}
          <SectionCard title="Route">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3">
              <MapPin className="w-4 h-4 text-primary" /><span>{d.pickup}</span>
              <ArrowRight className="w-4 h-4 text-slate-400" /><span>{d.destination}</span>
            </div>
            {d.live_tracking && (
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-rose-600" data-testid="live-indicator">
                <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span></span>
                Live location active{d.role === "traveller" ? " — sharing your GPS" : ""}{d.live_location?.at ? ` · updated ${new Date(d.live_location.at).toLocaleTimeString()}` : ""}
              </div>
            )}
            {(d.pickup_lat != null || d.dest_lat != null || d.live_location) && (
              <MapView origin={d.pickup_lat != null ? { lat: d.pickup_lat, lng: d.pickup_lng } : null} destination={d.dest_lat != null ? { lat: d.dest_lat, lng: d.dest_lng } : null} originLabel="Pickup" destinationLabel="Destination" height={240} live={d.live_tracking && d.live_location ? { lat: d.live_location.lat, lng: d.live_location.lng } : null} />
            )}
          </SectionCard>

          <SectionCard title="Package">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              {[["Description", d.description], ["Category", d.category], ["Size", d.size], ["Weight", d.weight], ["Date", d.delivery_date], ["Notes", d.notes || "—"]].map(([k, v]) => (
                <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="font-medium text-slate-800">{v}</p></div>
              ))}
            </div>
            {d.package_photo_id && <div className="mt-4"><p className="text-xs text-slate-400 mb-1.5">Package photo</p><Photo id={d.package_photo_id} alt="Package" /></div>}
          </SectionCard>

          <SectionCard title={<span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" /> Payment</span>}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">{d.role === "traveller" ? "Collect on delivery" : "Pay on delivery"}</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="payment-amount">₹{d.price_amount ?? "—"}</p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${d.payment_status === "collected" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`} data-testid="payment-status">{d.payment_status === "collected" ? "Cash collected" : "Cash on Delivery"}</span>
                {d.distance_km != null && <p className="mt-1 text-xs text-slate-400">~{d.distance_km} km</p>}
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">{d.role === "traveller" ? "Collect this amount in cash from the recipient when you hand over the package." : "Pay this amount in cash to the traveller once the package is delivered."}</p>
          </SectionCard>

          {d.role === "sender" && (d.pickup_pin || d.delivery_pin) && (            <SectionCard title={<span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Handover codes</span>}>
              <p className="text-xs text-slate-500 mb-4">Show the <b>Pickup code</b> to the traveller when they collect the package, and share the <b>Delivery code</b> with the recipient at the destination. The traveller enters each code to confirm the step — this prevents wrong handovers.</p>
              <div className="grid grid-cols-2 gap-4">
                {d.pickup_pin && (
                  <div className="text-center rounded-xl border border-slate-100 p-3" data-testid="pickup-pin-card">
                    <QRCode value={`PACKMATE-PICKUP-${d.id}-${d.pickup_pin}`} />
                    <p className="mt-2 text-xs text-slate-400">Pickup PIN</p>
                    <p className="text-xl font-bold font-mono tracking-[0.3em] text-slate-800">{d.pickup_pin}</p>
                  </div>
                )}
                {d.delivery_pin && (
                  <div className="text-center rounded-xl border border-slate-100 p-3" data-testid="delivery-pin-card">
                    <QRCode value={`PACKMATE-DELIVERY-${d.id}-${d.delivery_pin}`} />
                    <p className="mt-2 text-xs text-slate-400">Delivery PIN</p>
                    <p className="text-xl font-bold font-mono tracking-[0.3em] text-slate-800">{d.delivery_pin}</p>
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          <SectionCard title="Status timeline">
            <ol className="relative border-l-2 border-slate-100 ml-2" data-testid="status-timeline">
              {TIMELINE_STEPS.map((s, i) => {
                const done = i <= curOrder;
                const at = (d.timeline || []).find((t) => t.state === s)?.at;
                return (
                  <li key={s} className="ml-6 pb-5 last:pb-0">
                    <span className={`absolute -left-[11px] w-5 h-5 rounded-full flex items-center justify-center ${done ? "bg-primary" : "bg-slate-200"}`}>
                      {done ? <CheckCircle2 className="w-3.5 h-3.5 text-white" /> : <Circle className="w-2 h-2 text-slate-400" />}
                    </span>
                    <p className={`text-sm ${done ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s}</p>
                    {done && at && <p className="text-xs text-slate-400">{new Date(at).toLocaleString()}</p>}
                  </li>
                );
              })}
            </ol>
          </SectionCard>

          {(d.pickup_confirmation || d.delivery_confirmation) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {d.pickup_confirmation && (
                <SectionCard title={<span className="flex items-center gap-2 text-emerald-600"><PackageCheck className="w-4 h-4" /> Pickup completed</span>}>
                  <p className="text-xs text-slate-500">{d.pickup_confirmation.address || d.pickup}</p>
                  <p className="text-xs text-slate-400 mt-1">{d.pickup_confirmation.at && new Date(d.pickup_confirmation.at).toLocaleString()}</p>
                  {d.pickup_confirmation.photo_id && <div className="mt-2"><Photo id={d.pickup_confirmation.photo_id} alt="Pickup proof" className="rounded-xl border border-slate-200 max-h-40 w-full object-cover" /></div>}
                </SectionCard>
              )}
              {d.delivery_confirmation && (
                <SectionCard title={<span className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Delivery completed</span>}>
                  <p className="text-xs text-slate-500">{d.delivery_confirmation.address || d.destination}</p>
                  <p className="text-xs text-slate-400 mt-1">{d.delivery_confirmation.at && new Date(d.delivery_confirmation.at).toLocaleString()}</p>
                  {d.delivery_confirmation.photo_id && <div className="mt-2"><Photo id={d.delivery_confirmation.photo_id} alt="Delivery proof" className="rounded-xl border border-slate-200 max-h-40 w-full object-cover" /></div>}
                </SectionCard>
              )}
            </div>
          )}

          {d.status === "Delivered" && d.review && (
            <SectionCard title={<span className="flex items-center gap-2"><StarRating value={d.review.my_review ? d.review.my_review.rating : 0} /> Rate your experience</span>} className="" >
              <div data-testid="review-card">
                {d.review.my_review ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="review-submitted">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-800">Your review for {d.review.reviewee_name}</p>
                      <StarRating value={d.review.my_review.rating} />
                    </div>
                    {d.review.my_review.comment && <p className="mt-2 text-sm text-slate-600">"{d.review.my_review.comment}"</p>}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">How was {d.review.reviewee_name}? Your feedback builds trust in the community.</p>
                    <StarRating value={rating} interactive onChange={setRating} size="w-7 h-7" />
                    <Textarea data-testid="review-comment-input" value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} rows={3} maxLength={500}
                      placeholder="Share a short review (optional) — packaging, punctuality, communication…" className="rounded-xl" />
                    <Button onClick={submitReview} disabled={busy || !rating} data-testid="review-submit-btn" className="rounded-xl">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit review"}
                    </Button>
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          {d.role === "sender" && offers.length > 0 && (
            <SectionCard title="Offers to deliver">
              <div className="space-y-3">
                {offers.map((o) => (
                  <div key={o.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100" data-testid={`offer-${o.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-primary font-semibold">{o.other_user?.name?.[0]}</div>
                      <div><p className="text-sm font-medium text-slate-800">{o.other_user?.name}</p>{o.message && <p className="text-xs text-slate-400">"{o.message}"</p>}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => respond(o.id, "accept")} disabled={busy} data-testid={`offer-accept-btn-${o.id}`} className="rounded-lg">Accept</Button>
                      <Button size="sm" variant="outline" onClick={() => respond(o.id, "decline")} disabled={busy} data-testid={`offer-decline-btn-${o.id}`} className="rounded-lg">Decline</Button>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>

        <div className="space-y-6">
          <SectionCard title="Traveller / Driver">
            {d.traveller ? (
              <div className="space-y-3" data-testid="traveller-details">
                <div className="flex items-center gap-3">
                  {avatarSrc(d.traveller) ? <img src={avatarSrc(d.traveller)} alt={d.traveller.name} className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-primary font-semibold"><Truck className="w-5 h-5" /></div>}
                  <div>
                    <p className="text-sm font-medium text-slate-800 flex items-center gap-1">{d.traveller.name}{d.traveller.verification_status === "verified" && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />}</p>
                    <p className="text-xs text-slate-400 capitalize">{d.traveller.verification_status === "verified" ? "Verified" : "Not verified"}</p>
                  </div>
                </div>
                {d.traveller.phone && <p className="text-sm text-slate-600 flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-primary" /> {d.traveller.phone}</p>}
                {tv.travel_mode && <p className="text-sm text-slate-600 flex items-center gap-2"><Car className="w-3.5 h-3.5 text-primary" /> {tv.travel_mode}{tv.vehicle_model ? ` · ${tv.vehicle_model}` : ""}</p>}
                {tv.vehicle_reg && <p className="text-xs text-slate-500">Reg: <span className="font-mono">{tv.vehicle_reg}</span></p>}
                {tv.vehicle_photo_id && <Photo id={tv.vehicle_photo_id} alt="Vehicle" className="rounded-xl border border-slate-200 max-h-32 w-full object-cover" />}
              </div>
            ) : <p className="text-sm text-slate-400">No traveller connected yet.</p>}
          </SectionCard>

          <SectionCard title="Sender">
            <div className="flex items-center gap-3">
              {avatarSrc(d.sender) ? <img src={avatarSrc(d.sender)} alt={d.sender?.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-primary font-semibold"><User className="w-5 h-5" /></div>}
              <div><p className="text-sm font-medium text-slate-800 flex items-center gap-1">{d.sender?.name}{d.sender?.verification_status === "verified" && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />}</p>{d.sender?.phone && <p className="text-xs text-slate-400">{d.sender.phone}</p>}</div>
            </div>
          </SectionCard>

          <SectionCard title="Actions">
            <div className="space-y-2">
              {d.connection_id && <Button onClick={() => navigate("/messages")} className="w-full rounded-xl" data-testid="dd-message-btn"><MessageSquare className="w-4 h-4" /> Open chat</Button>}
              {d.role === "traveller" && d.status === "Accepted" && <Button onClick={() => { setPhoto(null); setPickupOpen(true); }} disabled={busy} className="w-full rounded-xl" data-testid="dd-pickup-btn"><Camera className="w-4 h-4" /> Confirm Pickup</Button>}
              {d.role === "traveller" && d.status === "Picked Up" && <Button variant="outline" onClick={() => changeStatus("In Transit")} disabled={busy} className="w-full rounded-xl" data-testid="dd-intransit-btn">Mark In Transit</Button>}
              {d.role === "traveller" && d.status === "In Transit" && <Button variant="outline" onClick={() => changeStatus("Arrived")} disabled={busy} className="w-full rounded-xl" data-testid="dd-arrived-btn">Mark Arrived</Button>}
              {d.can_manage && canConfirmDeliver && <Button onClick={() => { setPhoto(null); setDeliverOpen(true); }} disabled={busy} className="w-full rounded-xl" data-testid="dd-delivered-btn"><PackageCheck className="w-4 h-4" /> Confirm Delivery</Button>}
              {canCancel && <Button variant="ghost" onClick={() => { setCReason(""); setCOther(""); setCancelOpen(true); }} disabled={busy} className="w-full rounded-xl text-rose-600" data-testid="dd-cancel-btn">{d.role === "traveller" ? "Back Out of Delivery" : "Cancel Delivery"}</Button>}
              <Button variant="ghost" onClick={() => setReportOpen(true)} className="w-full rounded-xl text-slate-500" data-testid="dd-report-btn"><Flag className="w-4 h-4" /> Report</Button>
            </div>
          </SectionCard>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
            <p className="font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" /> Safety</p>
            Meet in public places, inspect packages before accepting, and never carry prohibited items. Pack Mate cannot guarantee the conduct of other users.
          </div>
        </div>
      </div>

      {/* Pickup dialog */}
      <Dialog open={pickupOpen} onOpenChange={setPickupOpen}>
        <DialogContent data-testid="pickup-dialog">
          <DialogHeader><DialogTitle>Confirm pickup</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" /> {d.pickup}</p>
          <p className="text-xs text-slate-400">{d.description} · {d.category} · {d.weight}</p>
          <Label className="text-sm">Pickup PIN <span className="text-rose-500">*</span></Label>
          <Input data-testid="pickup-pin-input" value={pickupPin} onChange={(e) => setPickupPin(e.target.value)} placeholder="6-digit code from the sender" inputMode="numeric" maxLength={6} className="rounded-xl font-mono tracking-widest" />
          <Label className="text-sm">Package photo at pickup <span className="text-slate-400 font-normal">(optional)</span></Label>
          <ImageUpload value={photo} onChange={setPhoto} purpose="pickup" testid="pickup-photo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickupOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={confirmPickup} disabled={busy} className="rounded-xl" data-testid="pickup-confirm-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Pickup"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery dialog */}
      <Dialog open={deliverOpen} onOpenChange={setDeliverOpen}>
        <DialogContent data-testid="deliver-dialog">
          <DialogHeader><DialogTitle>Confirm delivery</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-emerald-500" /> {d.destination}</p>
          <Label className="text-sm">Delivery PIN <span className="text-rose-500">*</span></Label>
          <Input data-testid="deliver-pin-input" value={deliverPin} onChange={(e) => setDeliverPin(e.target.value)} placeholder="6-digit code from the recipient" inputMode="numeric" maxLength={6} className="rounded-xl font-mono tracking-widest" />
          <Label className="text-sm">Delivery proof photo <span className="text-slate-400 font-normal">(optional)</span></Label>
          <ImageUpload value={photo} onChange={setPhoto} purpose="delivery" testid="delivery-photo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliverOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={confirmDelivery} disabled={busy} className="rounded-xl" data-testid="deliver-confirm-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Delivery"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="delivery-cancel-dialog">
          <DialogHeader><DialogTitle>{d.role === "traveller" ? "Back out of this delivery?" : "Cancel this delivery request?"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{d.role === "traveller"
            ? "You will be removed as the traveller. The delivery reopens for other travellers and the sender is notified."
            : <>This will move the request to Cancelled{d.traveller ? " and notify the connected traveller" : ""}. This cannot be undone.</>}</p>
          <Label className="text-sm">Reason (optional)</Label>
          <Select value={cReason} onValueChange={setCReason}>
            <SelectTrigger data-testid="delivery-cancel-reason" className="rounded-xl"><SelectValue placeholder="Select a reason" /></SelectTrigger>
            <SelectContent>{CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          {cReason === "Other" && <Textarea value={cOther} onChange={(e) => setCOther(e.target.value)} placeholder="Tell us more" className="rounded-xl" rows={2} data-testid="delivery-cancel-other" />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="rounded-xl" data-testid="delivery-cancel-keep">Keep Request</Button>
            <Button onClick={doCancel} disabled={busy} className="rounded-xl bg-rose-600 hover:bg-rose-700" data-testid="delivery-cancel-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (d.role === "traveller" ? "Back Out" : "Cancel Delivery")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>Report this delivery</DialogTitle></DialogHeader>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="report-reason-select" className="rounded-xl"><SelectValue placeholder="Select a reason" /></SelectTrigger>
            <SelectContent>{REPORT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea data-testid="report-description-input" value={reportDesc} onChange={(e) => setReportDesc(e.target.value)} placeholder="Add details (optional)" className="rounded-xl" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitReport} className="rounded-xl" data-testid="report-submit-btn">Submit report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
